import {
  EQUIPMENT,
  EQUIPMENT_MESSAGE,
  EXERCISE_TYPE,
  EXERCISE_TYPE_MESSAGE,
  getEquipmentFiltersExercisesSelect,
  getFoundExercisesSelector,
  getMusclesFiltersExercisesSelect,
  getPageExerciseUiSelect,
  getQueryExercisesSelect,
  getTypesFiltersExercisesSelector,
  loadExercisesAction,
  MUSCLE,
  MUSCLE_MESSAGE,
  resetFiltersExercisesAction,
  updatePageExerciseUiAction,
  updateQueryExercisesUiAction
} from '@gymaholic/packages'
import Button from 'components/Button'
import { ArrowDownIcon } from 'components/Icon/icons/arrow-down'
import { PlusIcon } from 'components/Icon/icons/plus'
import PageTitle from 'components/Page/Page.Title'
import Text from 'components/Text'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import openAppSignInModalAction from 'modules/modal/actions/open-app-sign-in'
import openModalAction from 'modules/modal/actions/open'
import { MODAL_ID } from 'modules/modal/constants/index.cjs'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import syncReduxToUrlAction from 'modules/training/modules/training-exercises/actions/sync-redux-to-url'
import { syncUrlWithReduxExerciseAction } from 'modules/training/modules/training-exercises/actions/sync-url-with-redux'
import { getCreatedExercisesSelector } from 'modules/training/modules/training-exercises/selectors'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import pushRouterAction from 'modules/router/actions/push'
import getLocationRouterSelect from 'modules/router/selectors/get-location'
import goBackRouterAction from 'modules/router/actions/go-back'
import FilterChip from 'modules/training/modules/training-exercises/components/Exercises/components/FilterChip/index.js'
import { debounceUtil } from 'packages/src'
import isReplacingExerciseRouterSelect from 'modules/router/selectors/is-replacing-exercise/index.js'
import getMatchRouterSelect from 'modules/router/selectors/get-match/index.js'
import { isLoggedInTrainingUserSelect } from 'modules/training/modules/training-users/selectors/is-logged-in/index.js'

const messages = defineMessages({
  exercises: {
    id: 'AddExercisesHeader.exercises',
    defaultMessage: 'Exercises'
  },
  replaceExercise: {
    id: 'AddExercisesHeader.replaceExercise',
    defaultMessage: 'Replace Exercise'
  },
  noExercisesFound: {
    id: 'AddExercisesHeader.noExercisesFound',
    defaultMessage: 'No exercises found'
  },
  addExercise: {
    id: 'AddExercisesHeader.addExercise',
    defaultMessage: 'Add Exercise'
  },
  searchExercise: {
    id: 'AddExercisesHeader.searchExercise',
    defaultMessage: 'Search Exercises'
  },
  filterMuscle: {
    id: 'AddExercisesHeader.filterMuscle',
    defaultMessage: 'Filter muscle'
  },
  filterEquipment: {
    id: 'AddExercisesHeader.filterEquipment',
    defaultMessage: 'Filter equipment'
  },
  muscles: {
    id: 'AddExercisesHeader.muscles',
    defaultMessage: 'Muscles'
  },
  equipment: {
    id: 'AddExercisesHeader.equipment',
    defaultMessage: 'Equipment'
  },
  types: {
    id: 'AddExercisesHeader.types',
    defaultMessage: 'Types'
  }
})

/**
 * @param isReadOnly
 * @param isView
 * @returns {JSX.Element}
 * @constructor
 */
export default function AddExercisesHeader ({
  isReadOnly,
  isView
}) {
  let muscleUrlParam
  const match = useSelector(getMatchRouterSelect)
  if (match.params.muscle !== undefined) {
    muscleUrlParam = MUSCLE[match.params.muscle.replace(/ /g, '_')
      .toUpperCase()] || null
  }
  let equipmentUrlParam
  if (match.params.equipment !== undefined) {
    equipmentUrlParam = EQUIPMENT[match.params.equipment.replace(
      / /g,
      '_'
    ).toUpperCase()] || null
  }
  let typeUrlParam
  if (match.params.type !== undefined) {
    typeUrlParam = EXERCISE_TYPE[match.params.type.replace(/ /g, '_')
      .toUpperCase()] || null
  }
  const intl = useIntl()
  const dispatch = useDispatch()
  let params
  if (
    muscleUrlParam !== undefined ||
    equipmentUrlParam !== undefined ||
    typeUrlParam !== undefined
  ) {
    params = {
      equipmentFilters: equipmentUrlParam !== undefined
        ? [equipmentUrlParam]
        : [],
      muscleFilters: muscleUrlParam !== undefined ? [muscleUrlParam] : [],
      typesFilters: typeUrlParam !== undefined ? [typeUrlParam] : []
    }
  }
  let exercises = useSelector(getFoundExercisesSelector(params))

  const currentPage = useSelector(getPageExerciseUiSelect)
  const createdExercises = useSelector(getCreatedExercisesSelector())
  const isLoggedIn = useSelector(isLoggedInTrainingUserSelect)
  const query = useSelector(getQueryExercisesSelect)
  const musclesFilters = useSelector(getMusclesFiltersExercisesSelect)
  const equipmentFilters = useSelector(getEquipmentFiltersExercisesSelect)
  const typesFilters = useSelector(getTypesFiltersExercisesSelector)
  const searchInputRef = React.createRef()
  const location = useSelector(getLocationRouterSelect)
  const locationState = location?.state
  const isReplacing = useSelector(isReplacingExerciseRouterSelect)

  if (createdExercises &&
    createdExercises.data &&
    createdExercises.data.length >
    0) {
    createdExercises.data.map((exerciseId) => {
      const index = exercises.indexOf(exerciseId)
      exercises.splice(index, 1)
      exercises = [
        exerciseId,
        ...exercises
      ]
      return null
    })
  }

  React.useEffect(() => {
    dispatch(updatePageExerciseUiAction(1))
    dispatch(syncUrlWithReduxExerciseAction())
    return () => {
      dispatch(resetFiltersExercisesAction())
      dispatch(updateQueryExercisesUiAction(''))
    }
  }, [])

  const debouncedSearch = React.useRef(
    debounceUtil(async (q) => {
      if (currentPage !== 1) {
        return
      }

      dispatch(
        loadExercisesAction({
          query: { q },
          shouldExcludeIds: true
        })
      )
    }, 300)
  ).current

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  if (
    muscleUrlParam === null ||
    equipmentUrlParam === null ||
    typeUrlParam === null
  ) {
    pushRouterAction({
      pathname: ROUTE_PATH.EXERCISES
    })
  }

  const handleClickGoBack = () => {
    dispatch(goBackRouterAction())
  }

  const handleClickAdd = () => {
    if (!isLoggedIn) {
      dispatch(openAppSignInModalAction())
      return
    }

    if (isReadOnly) {
      dispatch(
        pushRouterAction({
          pathname: ROUTE_PATH.CREATE_EXERCISE
        }, {
          isView: true
        })
      )
      return
    }

    dispatch(
      pushRouterAction({
        pathname: ROUTE_PATH.CREATE_EXERCISE
      }, {
        originMatch: locationState.originMatch,
        searchParams: locationState.searchParams
          ? locationState.searchParams
          : null
      })
    )
  }

  const handleChangeSearch = (event) => {
    dispatch(updateQueryExercisesUiAction(event.target.value))
    debouncedSearch(event.target.value)
  }

  const handleClickMuscle = () => {
    dispatch(
      openModalAction(MODAL_ID.MUSCLES_LIST, {
        label: formatMessageIntlUtil(intl, messages.filterMuscle),
        type: 'FILTER_MUSCLES',
        isReadOnly: isReadOnly
      })
    )
  }

  const handleClickEquipment = () => {
    dispatch(
      openModalAction(MODAL_ID.EQUIPMENT_LIST, {
        label: formatMessageIntlUtil(intl, messages.filterEquipment),
        type: 'FILTER_EQUIPMENT',
        isReadOnly: isReadOnly
      })
    )
  }

  const handleClickTypes = () => {
    dispatch(
      openModalAction(MODAL_ID.EXERCISE_TYPES_LIST, {
        isReadOnly: isReadOnly
      })
    )
  }

  const handleClickResetFilters = () => {
    dispatch(resetFiltersExercisesAction())

    if (isReplacing) {
      return
    }

    const pathname = isReadOnly
      ? ROUTE_PATH.EXERCISES
      : ROUTE_PATH.ADD_EXERCISES_LIST

    dispatch(syncReduxToUrlAction(pathname))
  }

  const getMusclesFilterLabel = () => {
    if (musclesFilters.length === 1) {
      return formatMessageIntlUtil(intl, MUSCLE_MESSAGE[musclesFilters[0]])
    }

    if (musclesFilters.length > 1) {
      return formatMessageIntlUtil(intl, MUSCLE_MESSAGE[musclesFilters[0]]) +
        ' +'
    }

    return formatMessageIntlUtil(intl, messages.muscles)
  }

  const getEquipmentFilterLabel = () => {
    if (equipmentFilters.length === 1) {
      return formatMessageIntlUtil(intl, EQUIPMENT_MESSAGE[equipmentFilters[0]])
    }

    if (equipmentFilters.length > 1) {
      return formatMessageIntlUtil(
        intl,
        EQUIPMENT_MESSAGE[equipmentFilters[0]]
      ) + ' +'
    }

    return formatMessageIntlUtil(intl, messages.equipment)
  }

  const getTypesFilterLabel = () => {
    if (typesFilters.length === 1) {
      return formatMessageIntlUtil(intl, EXERCISE_TYPE_MESSAGE[typesFilters[0]])
    }

    if (typesFilters.length > 1) {
      return formatMessageIntlUtil(
        intl,
        EXERCISE_TYPE_MESSAGE[typesFilters[0]]
      ) + ' +'
    }

    return formatMessageIntlUtil(intl, messages.types)
  }

  let exercisesMsg = formatMessageIntlUtil(intl, messages.exercises)

  if (
    muscleUrlParam ||
    (
      musclesFilters.length ===
      1 &&
      equipmentFilters.length ===
      0 &&
      typesFilters.length ===
      0
    )) {
    exercisesMsg = formatMessageIntlUtil(
      intl,
      MUSCLE_MESSAGE[muscleUrlParam || musclesFilters[0]]
    ) + ' ' + exercisesMsg
  } else if (
    equipmentUrlParam ||
    (
      equipmentFilters.length ===
      1 &&
      musclesFilters.length ===
      0 &&
      typesFilters.length ===
      0
    )) {
    exercisesMsg = formatMessageIntlUtil(
      intl,
      EQUIPMENT_MESSAGE[equipmentUrlParam || equipmentFilters[0]]
    ) + ' ' + exercisesMsg
  } else if (typeUrlParam ||
    (
      typesFilters.length ===
      1 &&
      musclesFilters.length ===
      0 &&
      equipmentFilters.length ===
      0
    )) {
    exercisesMsg = formatMessageIntlUtil(
      intl,
      EXERCISE_TYPE_MESSAGE[typeUrlParam || typesFilters[0]]
    ) + ' ' + exercisesMsg
  }

  let titleMessage
  if (isView) {
    titleMessage = messages.exercises
  } else if (isReplacing) {
    titleMessage = messages.replaceExercise
  } else {
    titleMessage = messages.addExercise
  }

  return (
    <>
      {
        isReadOnly
          ? (
            <View
              mt={2}
              row
              ac
              style={{ justifyContent: 'space-between' }}
            >
              <PageTitle>
                {exercisesMsg}
              </PageTitle>

              {
                !isReplacing && (
                  <Button
                    onClick={handleClickAdd}
                    style={{
                      width: 'unset',
                      height: 'unset',
                      maxWidth: 'unset',
                      maxHeight: 'unset'
                    }}
                  >
                    <PlusIcon
                      fill='#000000'
                      style={{
                        width: '15px',
                        height: '15px'
                      }}
                    />
                  </Button>
                )
              }
            </View>
            )
          : (
            <View
              py={2}
              row
              style={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <View style={{ width: '100px' }}>
                <Button
                  onClick={handleClickGoBack}
                  style={{
                    width: 'unset',
                    height: 'unset',
                    maxWidth: 'unset',
                    maxHeight: 'unset'
                  }}
                >
                  <ArrowDownIcon
                    style={{
                      width: '18px',
                      height: '18px',
                      transform: 'rotate(90deg)'
                    }}
                  />
                </Button>
              </View>
              <Text
                fw='500'
                fs='1.3em'
                style={{ textAlign: 'center' }}
              >
                {
                  formatMessageIntlUtil(
                    intl,
                    titleMessage
                  )
                }
              </Text>

              <View
                style={{
                  width: '100px',
                  textAlign: 'end'
                }}
              >
                {
                  !isReplacing && (
                    <Button
                      onClick={handleClickAdd}
                      d='flex'
                      style={{
                        width: 'unset',
                        height: 'unset',
                        maxWidth: 'unset',
                        maxHeight: 'unset',
                        justifyContent: 'flex-end'
                      }}
                    >
                      <PlusIcon
                        fill='#000000'
                        style={{
                          width: '15px',
                          height: '15px'
                        }}
                      />
                    </Button>
                  )
                }
              </View>
            </View>
            )
      }
      <View
        mb={2}
      >
        <input
          ref={searchInputRef}
          type='text'
          value={query}
          onChange={handleChangeSearch}
          style={{
            marginTop: '10px',
            fontSize: '1.2em',
            padding: '10px',
            borderRadius: '5px',
            background: '#f6f6f6'
          }}
          placeholder={formatMessageIntlUtil(
            intl,
            messages.searchExercise
          )}
        />
        <View
          mt={1}
          row
          ac
          style={{ flexWrap: 'wrap' }}
        >
          {(
            musclesFilters.length >
            0 ||
            equipmentFilters.length >
            0 ||
            typesFilters.length >
            0
          ) && (
            <FilterChip
              label='Clear'
              iconHidden
              onClick={handleClickResetFilters}
            />
          )}
          <FilterChip
            label={getMusclesFilterLabel()}
            onClick={handleClickMuscle}
          />
          <FilterChip
            label={getEquipmentFilterLabel()}
            onClick={handleClickEquipment}
          />
          <FilterChip
            label={getTypesFilterLabel()}
            onClick={handleClickTypes}
          />
        </View>
      </View>

    </>
  )
}
